import { additionalIds, additionalProfiles } from "./scenarioCatalog";
import type { Incident, Vehicle } from "./model";

export const profileIds = [
  "brand-klein",
  "brand-wohnung",
  "brand-gross",
  "vu-leicht",
  "vu-eingeklemmt",
  "vu-manv",
  "rd-standard",
  "rd-notarzt",
  "rd-reanimation",
  "pol-streife",
  "pol-spezial",
  "wasser-person",
  "berg-person",
  "unwetter-klein",
  "unwetter-gross",
  ...additionalIds,
] as const;
export type ProfileId = (typeof profileIds)[number];
export type Profile = {
  type: Incident["type"];
  title: string;
  report: string;
  weight: number;
  priority: 1 | 2;
  needs: Partial<Record<Vehicle["kind"], number>>;
  workSeconds: number;
  spread?: boolean;
  code?: string;
  patients?: { count: number; critical: number; specialty: Specialty };
};
export type Specialty =
  | "general"
  | "trauma"
  | "cardiac"
  | "stroke"
  | "burn"
  | "pediatric";
export const incidentProfiles: Record<ProfileId, Profile> = {
  ...additionalProfiles,
  "brand-klein": {
    type: "brand",
    title: "Kleinbrand im Freien",
    weight: 55,
    priority: 2,
    needs: { LF: 1 },
    workSeconds: 120,
    report:
      "Ein Abfallbehälter brennt im Freien. Kein Gebäude betroffen, keine Personen gefährdet.",
  },
  "brand-wohnung": {
    type: "brand",
    title: "Wohnungsbrand · Menschenrettung",
    weight: 40,
    priority: 1,
    needs: { HLF: 1, DLK: 1, RTW: 1 },
    workSeconds: 240,
    spread: true,
    report:
      "Dichter Rauch aus einer Wohnung im zweiten Obergeschoss. Eine Person am Fenster, Treppenraum verraucht. Feuerwehr zur Menschenrettung und Rettungsdienst benötigt.",
  },
  "brand-gross": {
    type: "brand",
    title: "Hallenbrand · Großeinsatz",
    weight: 5,
    priority: 1,
    needs: { HLF: 3, DLK: 1, ELW: 1, RTW: 2 },
    workSeconds: 480,
    spread: true,
    report:
      "Ausgedehnter Brand einer Gewerbehalle. Mehrere Beschäftigte haben Rauch eingeatmet. Einsatzleitung und zusätzliche Löschkräfte erforderlich.",
  },
  "vu-leicht": {
    type: "vu",
    title: "Verkehrsunfall · Eine verletzte Person",
    weight: 70,
    priority: 2,
    needs: { RTW: 1 },
    workSeconds: 180,
    report:
      "Auffahrunfall. Eine ansprechbare Person mit Schmerzen. Niemand eingeklemmt, kein Brand und keine auslaufenden Betriebsstoffe gemeldet.",
  },
  "vu-eingeklemmt": {
    type: "vu",
    title: "Verkehrsunfall · Person eingeklemmt",
    weight: 25,
    priority: 1,
    needs: { HLF: 1, RTW: 1, NEF: 1 },
    workSeconds: 180,
    report:
      "Zwei PKW kollidiert. Eine schwer verletzte Person eingeklemmt, weitere Beteiligte unverletzt. Technische Rettung und notärztliche Versorgung erforderlich.",
  },
  "vu-manv": {
    type: "vu",
    title: "Verkehrsunfall · Mehrere Verletzte",
    weight: 5,
    priority: 1,
    needs: { HLF: 2, RTW: 4, NEF: 2, ELW: 1 },
    workSeconds: 480,
    report:
      "Unfall mit mehreren Fahrzeugen. Vier Verletzte gemeldet, darunter zwei Schwerverletzte; technische Rettung nötig. Führungsdienst und weitere Rettungsmittel nachführen.",
  },
  "rd-standard": {
    type: "medizinisch",
    title: "Rettungsdienst · Akute Erkrankung",
    weight: 70,
    priority: 2,
    needs: { RTW: 1 },
    workSeconds: 180,
    report:
      "Eine Person ist akut erkrankt, ansprechbar und atmet unauffällig. Rettungsdienstliche Untersuchung erforderlich; derzeit kein Notarztbedarf gemeldet.",
  },
  "rd-notarzt": {
    type: "medizinisch",
    title: "Rettungsdienst · Notarztindikation",
    weight: 25,
    priority: 1,
    needs: { RTW: 1, NEF: 1 },
    workSeconds: 240,
    report:
      "Eine Person mit schwerer Atemnot. RTW und Notarzt erforderlich. Ein verfügbarer HvO kann die Zeit bis zum Eintreffen überbrücken.",
  },
  "rd-reanimation": {
    type: "medizinisch",
    title: "Rettungsdienst · Reanimation",
    weight: 5,
    priority: 1,
    needs: { RTW: 1, NEF: 1 },
    workSeconds: 360,
    report:
      "Bewusstlose Person, keine normale Atmung. Reanimation durch Ersthelfer begonnen. RTW und Notarzt sowie verfügbarer HvO zur frühzeitigen Hilfe benötigt.",
  },
  "pol-streife": {
    type: "polizei",
    title: "Polizeiliche Hilfe · Streifenwagen",
    weight: 95,
    priority: 2,
    needs: { FuStW: 1 },
    workSeconds: 180,
    report:
      "Polizeiliche Klärung einer Streitigkeit angefordert. Keine Waffen und keine Verletzten gemeldet.",
  },
  "pol-spezial": {
    type: "polizei",
    title: "Besondere Polizeilage · Spezialkräfte",
    weight: 5,
    priority: 1,
    needs: { FuStW: 2, SEK: 1, RTW: 1 },
    workSeconds: 420,
    report:
      "Bewaffnete Bedrohung gemeldet. Spezialeinheit, polizeiliche Absicherung und rettungsdienstliche Bereitstellung angefordert.",
  },
  "wasser-person": {
    type: "wasser",
    title: "Wasserrettung · Person in Not",
    weight: 1,
    priority: 1,
    needs: { "GW-Wasser": 1, RTW: 1, NEF: 1 },
    workSeconds: 300,
    report:
      "Person im Wasser in Not. Wasserrettung und Rettungsdienst mit Notarzt erforderlich.",
  },
  "berg-person": {
    type: "berg",
    title: "Bergrettung · Verletzte Person",
    weight: 1,
    priority: 1,
    needs: { Bergrettung: 1, RTW: 1, NEF: 1 },
    workSeconds: 300,
    report:
      "Schwer verletzte Person im unwegsamen Gelände. Bergrettung und notärztliche Versorgung erforderlich.",
  },
  "unwetter-klein": {
    type: "unwetter",
    title: "Unwetter · Baum auf Fahrbahn",
    weight: 90,
    priority: 2,
    needs: { HLF: 1 },
    workSeconds: 180,
    report:
      "Ein Baum blockiert die Fahrbahn. Keine Verletzten, keine Stromleitung betroffen. Feuerwehr zur technischen Hilfe angefordert.",
  },
  "unwetter-gross": {
    type: "unwetter",
    title: "Unwetter · Gebäudeschaden",
    weight: 10,
    priority: 1,
    needs: { GKW: 1, HLF: 1, ELW: 1 },
    workSeconds: 420,
    report:
      "Gebäude nach Sturm beschädigt. Feuerwehr und THW zur technischen Sicherung angefordert. Keine verletzten Personen gemeldet.",
  },
};
export const defaultProfiles: Record<Incident["type"], ProfileId> = {
  brand: "brand-wohnung",
  vu: "vu-eingeklemmt",
  medizinisch: "rd-standard",
  polizei: "pol-streife",
  wasser: "wasser-person",
  berg: "berg-person",
  unwetter: "unwetter-klein",
};
// Saved pre-profile calls used the old high-acuity templates. Keep their documented indication.
const legacyProfiles: Record<Incident["type"], ProfileId> = {
  ...defaultProfiles,
  medizinisch: "rd-notarzt",
  polizei: "pol-spezial",
  unwetter: "unwetter-gross",
};
export function profileFor(i: Pick<Incident, "type" | "profileId">) {
  return incidentProfiles[i.profileId ?? legacyProfiles[i.type]];
}
export function chooseProfile(type: Incident["type"], roll: number): ProfileId {
  const rows = profileIds.filter((id) => incidentProfiles[id].type === type);
  let remaining =
    roll * rows.reduce((n, id) => n + incidentProfiles[id].weight, 0);
  return (
    rows.find((id) => (remaining -= incidentProfiles[id].weight) < 0) ??
    rows[rows.length - 1]
  );
}
export function profileRequirements(i: Incident) {
  const profile = profileFor(i);
  const needs = { ...profile.needs };
  if (profile.spread && i.escalation >= 1) needs.HLF = (needs.HLF ?? 0) + 1;
  // Once treatment is finished, transporting crews no longer count as missing scene crews.
  if (i.patients?.length) {
    const waiting = i.patients.filter((p) => p.phase === "waiting").length;
    if (needs.RTW) needs.RTW = Math.min(needs.RTW, waiting);
    if (!waiting) {
      delete needs.RTW;
      delete needs.NEF;
    }
  }
  if (i.sceneResolvedAt !== undefined) {
    if (!i.patients?.some((p) => p.phase === "waiting")) return {};
    return Object.fromEntries(
      Object.entries(needs).filter(([kind]) =>
        ["RTW", "KTW", "NEF", "RTH", "ITH"].includes(kind),
      ),
    ) as Partial<Record<Vehicle["kind"], number>>;
  }
  return needs;
}

export function incidentCode(i: Pick<Incident, "type" | "profileId">) {
  const codes: Partial<Record<ProfileId, string>> = {
    "brand-klein": "B1",
    "brand-wohnung": "B3",
    "brand-gross": "B4",
    "vu-leicht": "RD1",
    "vu-eingeklemmt": "TH3",
    "vu-manv": "MANV",
    "rd-standard": "RD1",
    "rd-notarzt": "RD2",
    "rd-reanimation": "REA",
    "pol-streife": "POL1",
    "pol-spezial": "POL3",
    "wasser-person": "WR2",
    "berg-person": "BR2",
    "unwetter-klein": "TH1",
    "unwetter-gross": "TH3",
  };
  return (
    profileFor(i).code ?? codes[i.profileId ?? legacyProfiles[i.type]] ?? "EINS"
  );
}

// Patients are simulation cases, never real patient data.
const legacyPatients: Partial<Record<ProfileId, Profile["patients"]>> = {
  "brand-wohnung": { count: 1, critical: 0, specialty: "burn" },
  "brand-gross": { count: 2, critical: 1, specialty: "burn" },
  "vu-leicht": { count: 1, critical: 0, specialty: "trauma" },
  "vu-eingeklemmt": { count: 1, critical: 1, specialty: "trauma" },
  "vu-manv": { count: 4, critical: 2, specialty: "trauma" },
  "rd-standard": { count: 1, critical: 0, specialty: "general" },
  "rd-notarzt": { count: 1, critical: 1, specialty: "general" },
  "rd-reanimation": { count: 1, critical: 1, specialty: "cardiac" },
  "wasser-person": { count: 1, critical: 1, specialty: "general" },
  "berg-person": { count: 1, critical: 1, specialty: "trauma" },
};
export function patientPlan(id: ProfileId) {
  return incidentProfiles[id].patients ?? legacyPatients[id];
}
export const escalationPaths: Partial<
  Record<ProfileId, { to: ProfileId; chance: number; text: string }>
> = {
  "brand-klein": {
    to: "brand-garage",
    chance: 0.25,
    text: "Feuer greift auf eine angrenzende Garage über.",
  },
  "brand-hecke": {
    to: "brand-garage",
    chance: 0.3,
    text: "Flammen haben ein angrenzendes Gebäude erreicht.",
  },
  "brand-garage": {
    to: "brand-wohnung",
    chance: 0.3,
    text: "Rauch dringt in das Wohnhaus ein. Eine Person eingeschlossen.",
  },
  "brand-kueche": {
    to: "brand-mehrfamilie",
    chance: 0.3,
    text: "Treppenraum verraucht. Weitere Menschen benötigen Hilfe.",
  },
  "brand-keller": {
    to: "brand-mehrfamilie",
    chance: 0.3,
    text: "Rauch breitet sich im Treppenhaus aus. Weitere Bewohner eingeschlossen.",
  },
  "brand-wohnung": {
    to: "brand-dach",
    chance: 0.25,
    text: "Flammen erreichen den Dachstuhl. Weitere Verletzte entdeckt.",
  },
  "brand-dach": {
    to: "brand-block",
    chance: 0.2,
    text: "Feuer greift auf Nachbargebäude über. Größere Kräfte nachfordern.",
  },
  "brand-gross": {
    to: "brand-industrie",
    chance: 0.2,
    text: "Weitere Hallenteile erfasst. Mehrere zusätzliche Verletzte.",
  },
  "rd-standard": {
    to: "rd-notarzt",
    chance: 0.15,
    text: "Zustand verschlechtert sich. Notärztliche Versorgung notwendig.",
  },
  "rd-brustschmerz": {
    to: "rd-reanimation",
    chance: 0.2,
    text: "Patient nicht mehr ansprechbar. Reanimation erforderlich.",
  },
  "rd-notarzt": {
    to: "rd-reanimation",
    chance: 0.12,
    text: "Akute Verschlechterung. Reanimationslage gemeldet.",
  },
  "vu-leicht": {
    to: "vu-mehrere",
    chance: 0.12,
    text: "Weitere Verletzte nach Erkundung festgestellt.",
  },
  "vu-manv": {
    to: "manv-10",
    chance: 0.25,
    text: "Zahl der Verletzten höher als zunächst angenommen. MANV 10.",
  },
  "pol-streife": {
    to: "pol-randale",
    chance: 0.15,
    text: "Auseinandersetzung eskaliert. Verletzte, weitere Streifen und Rettungsdienst erforderlich.",
  },
  "pol-randale": {
    to: "pol-gruppe",
    chance: 0.2,
    text: "Weitere Gruppen beteiligt. Kräfte dringend nachfordern.",
  },
  "unwetter-klein": {
    to: "th-baum",
    chance: 0.15,
    text: "Unter dem Baum wird ein besetztes Fahrzeug entdeckt. Technische Rettung und Rettungsdienst nachfordern.",
  },
  "th-gas": {
    to: "brand-mehrfamilie",
    chance: 0.12,
    text: "Verpuffung im Gebäude. Brand und mehrere Verletzte gemeldet.",
  },
  "pol-bedrohung": {
    to: "pol-geisel",
    chance: 0.15,
    text: "Eine Person wird festgehalten. Lage als Geisellage neu bewerten.",
  },
  "wasser-person": {
    to: "wasser-bootsunfall",
    chance: 0.12,
    text: "Weitere Menschen im Wasser entdeckt. Zusätzliche Rettungsmittel erforderlich.",
  },
  "rd-allergie": {
    to: "rd-reanimation",
    chance: 0.12,
    text: "Patient nicht mehr ansprechbar. Reanimationslage gemeldet.",
  },
  "manv-10": {
    to: "manv-20",
    chance: 0.15,
    text: "Weitere Betroffene aufgefunden. MANV 20.",
  },
  "manv-20": {
    to: "manv-30",
    chance: 0.15,
    text: "Sichtung ergibt zusätzliche Verletzte. MANV 30.",
  },
  "manv-30": {
    to: "manv-40",
    chance: 0.12,
    text: "Weitere Verletzte gemeldet. MANV 40.",
  },
  "manv-40": { to: "manv-50", chance: 0.1, text: "Lage ausgeweitet. MANV 50." },
};
